/**
 * HTTP 路由服务 — API 服务层
 *
 * 对接后端 /api/v1/http-target-systems、/api/v1/http-route-rules、
 * /api/v1/http-route-logs、/api/v1/http-route-audit-logs、/api/v1/http-routes/debug
 *
 * 所有接口严格遵循后端数据契约，无硬编码假数据。
 */

import { request } from './request';
import type {
    HttpTargetSystem,
    HttpRouteRule,
    HttpRouteLog,
    HttpRouteAuditLog,
    RouteDebugInput,
    RouteDebugOutput,
} from '../types';

// ── Target Systems ──

/** 创建目标系统 */
export function createTargetSystem(data: Omit<HttpTargetSystem, 'id' | 'created_at' | 'updated_at'>): Promise<HttpTargetSystem> {
    return request<HttpTargetSystem>('/api/v1/http-target-systems', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

/** 获取目标系统列表 */
export function listTargetSystems(): Promise<HttpTargetSystem[]> {
    return request<HttpTargetSystem[]>('/api/v1/http-target-systems');
}

/** 查询单个目标系统 */
export function getTargetSystem(code: string): Promise<HttpTargetSystem> {
    return request<HttpTargetSystem>(`/api/v1/http-target-systems/${encodeURIComponent(code)}`);
}

/** 更新目标系统 */
export function updateTargetSystem(code: string, data: Partial<HttpTargetSystem>): Promise<HttpTargetSystem> {
    return request<HttpTargetSystem>(`/api/v1/http-target-systems/${encodeURIComponent(code)}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    });
}

/** 删除目标系统 */
export function deleteTargetSystem(code: string): Promise<void> {
    return request<void>(`/api/v1/http-target-systems/${encodeURIComponent(code)}`, {
        method: 'DELETE',
    });
}

// ── Route Rules ──

/** 创建路由规则 */
export function createRouteRule(data: Omit<HttpRouteRule, 'id' | 'created_at' | 'updated_at'>): Promise<HttpRouteRule> {
    return request<HttpRouteRule>('/api/v1/http-route-rules', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

/** 路由规则列表查询参数 */
export interface RouteRuleListParams {
    page?: number;
    page_size?: number;
}

/** 获取路由规则列表（单页） */
export function listRouteRules(params?: RouteRuleListParams): Promise<HttpRouteRule[]> {
    const searchParams = new URLSearchParams();
    if (params?.page !== undefined) {
        searchParams.set('page', String(params.page));
    }
    if (params?.page_size !== undefined) {
        searchParams.set('page_size', String(params.page_size));
    }
    const qs = searchParams.toString();
    return request<HttpRouteRule[]>(`/api/v1/http-route-rules${qs ? `?${qs}` : ''}`);
}

/** 拉取全部路由规则（自动翻页，避免默认 page_size=10 漏掉 POST 等低 ID 规则） */
export async function listAllRouteRules(pageSize = 50): Promise<HttpRouteRule[]> {
    const all: HttpRouteRule[] = [];
    let page = 1;
    for (;;) {
        const batch = await listRouteRules({ page, page_size: pageSize });
        if (!Array.isArray(batch) || batch.length === 0) {
            break;
        }
        all.push(...batch);
        if (batch.length < pageSize) {
            break;
        }
        page += 1;
    }
    return all;
}

/** 查询单个路由规则 */
export function getRouteRule(id: number): Promise<HttpRouteRule> {
    return request<HttpRouteRule>(`/api/v1/http-route-rules/${id}`);
}

/** 更新路由规则 */
export function updateRouteRule(id: number, data: Partial<HttpRouteRule>): Promise<HttpRouteRule> {
    return request<HttpRouteRule>(`/api/v1/http-route-rules/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
    });
}

/** 删除路由规则 */
export function deleteRouteRule(id: number): Promise<void> {
    return request<void>(`/api/v1/http-route-rules/${id}`, {
        method: 'DELETE',
    });
}

// ── Route Logs ──

/** 路由日志查询参数 */
export interface RouteLogListParams {
    sn?: string;
    trace_id?: string;
    source_path?: string;
    target_system_code?: string;
    success?: string;
    start_time?: string;
    end_time?: string;
    page?: number;
    page_size?: number;
}

/** 分页查询路由日志 */
export function listRouteLogs(params?: RouteLogListParams): Promise<HttpRouteLog[]> {
    const searchParams = new URLSearchParams();
    if (params) {
        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== '') {
                searchParams.set(key, String(value));
            }
        }
    }
    const qs = searchParams.toString();
    return request<HttpRouteLog[]>(`/api/v1/http-route-logs${qs ? `?${qs}` : ''}`);
}

/** 查询路由日志详情 */
export function getRouteLog(id: number): Promise<HttpRouteLog> {
    return request<HttpRouteLog>(`/api/v1/http-route-logs/${id}`);
}

// ── Audit Logs ──

/** 审计日志查询参数 */
export interface AuditLogListParams {
    object_type?: string;
    object_id?: string;
    page?: number;
    page_size?: number;
}

/** 查询操作审计日志 */
export function listAuditLogs(params?: AuditLogListParams): Promise<HttpRouteAuditLog[]> {
    const searchParams = new URLSearchParams();
    if (params) {
        for (const [key, value] of Object.entries(params)) {
            if (value !== undefined && value !== '') {
                searchParams.set(key, String(value));
            }
        }
    }
    const qs = searchParams.toString();
    return request<HttpRouteAuditLog[]>(`/api/v1/http-route-audit-logs${qs ? `?${qs}` : ''}`);
}

// ── Debug ──

/** 执行路由调试 */
export function debugRoute(input: RouteDebugInput): Promise<RouteDebugOutput> {
    return request<RouteDebugOutput>('/api/v1/http-routes/debug', {
        method: 'POST',
        body: JSON.stringify(input),
    });
}
